/**
 * voice-intake.ts
 * locx-voz-multi-intencion, Phase 1 (design.md D4/D8/D9) — shared Zod
 * contracts for the "Dictar" voice-intake feature.
 *
 * locx-voz-servicio-cross-tenant (2026-09-15) — D4: `servicio`/
 * `servicio_futuro` are now REACHABLE confirm branches, routed through the
 * cross-tenant partner path (`RequestServiceOrderAsPartnerUseCase`), NOT
 * `CreateServiceOrderUseCase`. The confirm vocabulary is collapsed into an
 * ALIAS of the classifier vocabulary — `VOICE_INTAKE_SUPPORTED_INTENTS =
 * VOICE_INTAKE_INTENTS` — so no consumer of either constant needs to change.
 * `intent_not_supported` stays in the failure-reason enum (prod rows carry
 * it) and the park branch in `ProcessVoiceIntakeUseCase` stays as
 * fail-closed defense, now permanently unreachable for these two intents.
 *
 * Previous scope cut (2026-09-15, `locx-voz-multi-intencion`'s own apply
 * run): the CLASSIFICATION vocabulary (`VOICE_INTAKE_INTENTS`) was already
 * 4-valued from day one — the classifier must be ABLE to recognize a
 * servicio/servicio_futuro dictation so it can route it correctly, rather
 * than mis-bucketing it into falla/repuesto. Only the CONFIRM vocabulary was
 * narrowed to 2 values back then; this change widens it back to 4.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Closed vocabularies
// ---------------------------------------------------------------------------

/** Full classifier-recognized vocabulary (design.md D8) — 4 values, ALL increments. */
export const VOICE_INTAKE_INTENTS = ['falla', 'repuesto', 'servicio', 'servicio_futuro'] as const;
export const VoiceIntakeIntentSchema = z.enum(VOICE_INTAKE_INTENTS);
export type VoiceIntakeIntent = z.infer<typeof VoiceIntakeIntentSchema>;

/**
 * Intents this increment actually routes to a domain destination — D4
 * (locx-voz-servicio-cross-tenant): an ALIAS of `VOICE_INTAKE_INTENTS`, not a
 * second literal list. All 4 classifier intents are now reachable at
 * confirm.
 */
export const VOICE_INTAKE_SUPPORTED_INTENTS = VOICE_INTAKE_INTENTS;
export const VoiceIntakeSupportedIntentSchema = VoiceIntakeIntentSchema;
export type VoiceIntakeSupportedIntent = VoiceIntakeIntent;

export const VOICE_INTAKE_STATUSES = [
  'captured',
  'transcribed',
  'classified',
  'needs_review',
  'dispatched',
  'discarded',
] as const;
export const VoiceIntakeStatusSchema = z.enum(VOICE_INTAKE_STATUSES);
export type VoiceIntakeStatus = z.infer<typeof VoiceIntakeStatusSchema>;

/**
 * 'intent_not_supported' is an ADDITION beyond design.md's 6-value failure
 * matrix (no_provider/asr_failed/asr_empty/llm_failed/llm_invalid_json/
 * low_confidence) — a direct consequence of this run's scope cut, named here
 * rather than silently reusing an unrelated reason.
 */
export const VOICE_INTAKE_FAILURE_REASONS = [
  'no_provider',
  'asr_failed',
  'asr_empty',
  'llm_failed',
  'llm_invalid_json',
  'low_confidence',
  'intent_not_supported',
] as const;
export const VoiceIntakeFailureReasonSchema = z.enum(VOICE_INTAKE_FAILURE_REASONS);
export type VoiceIntakeFailureReason = z.infer<typeof VoiceIntakeFailureReasonSchema>;

export const VOICE_INTAKE_MIN_CONFIDENCE = 0.6;

// ---------------------------------------------------------------------------
// POST /servicios/voice-intakes — ingest (D3: no model call)
// ---------------------------------------------------------------------------

export const VoiceIntakeIngestSchema = z
  .object({
    audioFileId: z.string().uuid(),
    /** Client-generated (D12) — UNIQUE(company_id, client_intake_id) backstop against replays. */
    clientIntakeId: z.string().uuid(),
    durationMs: z.coerce.number().int().nonnegative().optional(),
    assetId: z.string().uuid().optional(),
  })
  .strict();
export type VoiceIntakeIngest = z.infer<typeof VoiceIntakeIngestSchema>;

// ---------------------------------------------------------------------------
// Draft payload — D8 rule 1 (all-optional) + rule 2 (model never emits a
// uuid; only *_id fields the RESOLVER attaches carry one, alongside the
// *_score it computed — never silently applied, always a pre-seeded
// Combobox suggestion).
// ---------------------------------------------------------------------------

const scoreField = z.number().min(0).max(1).optional();

/**
 * locx-voz-polish, design.md D4 — one dictation, N fallas. Per-falla fields
 * only; the asset and the meter reading are hoisted to the root of
 * `VoiceDraftFallaFieldsCanonicalSchema` (shared, chosen once — mirrors
 * `VoiceDraftRepuestoFieldsSchema { items }`).
 */
export const VoiceDraftFallaItemSchema = z.object({
  failureTypeSpoken: z.string().optional(),
  failureTypeId: z.string().uuid().optional(),
  failureTypeScore: scoreField,
  description: z.string().optional(),
  importance: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
});
export type VoiceDraftFallaItem = z.infer<typeof VoiceDraftFallaItemSchema>;

const VoiceDraftFallaFieldsCanonicalSchema = z.object({
  assetSpoken: z.string().optional(),
  assetId: z.string().uuid().optional(),
  assetScore: scoreField,
  odometerHours: z.number().optional(),
  odometerKm: z.number().optional(),
  failures: z.array(VoiceDraftFallaItemSchema).optional(),
});

/**
 * D5 — tolerant read shim for `draft_payload` rows written before this
 * change (no `failures` key, per-falla fields flat at the root). Wraps the
 * legacy shape into a one-element `failures[]` array; already-new-shape
 * payloads pass through untouched. Zero rows are rewritten in the DB — this
 * runs at READ time, both for the runtime Zod parse path and the frontend
 * consumer, from a single implementation.
 */
export function normalizeVoiceDraftFallaFields(input: unknown): unknown {
  if (input === null || typeof input !== 'object') return input;
  const f = input as Record<string, unknown>;
  if (Array.isArray(f['failures'])) return f; // already new shape
  const { failureTypeSpoken, failureTypeId, failureTypeScore, description, importance, ...shared } = f;
  const legacy = { failureTypeSpoken, failureTypeId, failureTypeScore, description, importance };
  const hasAny = Object.values(legacy).some((v) => v !== undefined);
  return { ...shared, failures: hasAny ? [legacy] : [] };
}

export const VoiceDraftFallaFieldsSchema = z.preprocess(
  normalizeVoiceDraftFallaFields,
  VoiceDraftFallaFieldsCanonicalSchema,
);
export type VoiceDraftFallaFields = z.infer<typeof VoiceDraftFallaFieldsSchema>;

export const VoiceDraftRepuestoItemSchema = z.object({
  itemSpoken: z.string().optional(),
  itemMasterId: z.string().uuid().optional(),
  itemScore: scoreField,
  description: z.string().optional(),
  quantity: z.number().optional(),
  unit: z.string().optional(),
  assetSpoken: z.string().optional(),
  assetId: z.string().uuid().optional(),
  assetScore: scoreField,
});
export type VoiceDraftRepuestoItem = z.infer<typeof VoiceDraftRepuestoItemSchema>;

export const VoiceDraftRepuestoFieldsSchema = z.object({
  items: z.array(VoiceDraftRepuestoItemSchema).optional(),
  notes: z.string().optional(),
});
export type VoiceDraftRepuestoFields = z.infer<typeof VoiceDraftRepuestoFieldsSchema>;

/** Classifier draft shape — the confirm screen seeds its pickers from this as search strings only (D2), never a silently applied truth. */
export const VoiceDraftServicioFieldsSchema = z.object({
  clientSpoken: z.string().optional(),
  clientId: z.string().uuid().optional(),
  clientScore: scoreField,
  serviceTypeSpoken: z.string().optional(),
  serviceTypeId: z.string().uuid().optional(),
  serviceTypeScore: scoreField,
  scheduledAt: z.string().datetime().optional(),
  notes: z.string().optional(),
});
export type VoiceDraftServicioFields = z.infer<typeof VoiceDraftServicioFieldsSchema>;

export const VoiceIntakeDraftPayloadSchema = z.discriminatedUnion('intent', [
  z.object({ intent: z.literal('falla'), fields: VoiceDraftFallaFieldsSchema }),
  z.object({ intent: z.literal('repuesto'), fields: VoiceDraftRepuestoFieldsSchema }),
  z.object({ intent: z.literal('servicio'), fields: VoiceDraftServicioFieldsSchema }),
  z.object({ intent: z.literal('servicio_futuro'), fields: VoiceDraftServicioFieldsSchema }),
]);
export type VoiceIntakeDraftPayload = z.infer<typeof VoiceIntakeDraftPayloadSchema>;

/**
 * Raw shape the classifier port returns, BEFORE the discriminated union
 * above validates `fields` per-intent — kept loose (`z.record`) at this layer
 * because the model's structured-output call returns intent+fields+
 * unresolved together and per-intent field validation happens one level up
 * once `intent` is known (mirrors how VoiceIntakeDraftPayloadSchema is
 * assembled in the infrastructure layer, never in domain/application).
 */
export const VoiceIntakeClassificationResultSchema = z.object({
  intent: VoiceIntakeIntentSchema,
  confidence: z.number().min(0).max(1),
  fields: z.record(z.unknown()),
  unresolved: z.array(z.string()).default([]),
});
export type VoiceIntakeClassificationResult = z.infer<typeof VoiceIntakeClassificationResultSchema>;

// ---------------------------------------------------------------------------
// GET /servicios/voice-intakes/:id + POST .../process — response shape
// ---------------------------------------------------------------------------

export const VoiceIntakeResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  createdBy: z.string().uuid(),
  clientIntakeId: z.string().uuid(),
  audioFileId: z.string().uuid(),
  audioDurationMs: z.number().int().nullable(),
  assetId: z.string().uuid().nullable(),
  status: VoiceIntakeStatusSchema,
  transcript: z.string().nullable(),
  transcriptProvider: z.string().nullable(),
  promptVersion: z.string().nullable(),
  draftIntent: VoiceIntakeIntentSchema.nullable(),
  draftConfidence: z.number().min(0).max(1).nullable(),
  draftPayload: VoiceIntakeDraftPayloadSchema.nullable(),
  confirmedIntent: VoiceIntakeIntentSchema.nullable(),
  confirmedPayload: z.record(z.unknown()).nullable(),
  failureReason: VoiceIntakeFailureReasonSchema.nullable(),
  dispatchedEntityKind: z.enum(['fleet_failure', 'purchase_case', 'service_order', 'service_order_request']).nullable(),
  dispatchedEntityId: z.string().uuid().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
  /**
   * D9.3 — intents the CALLER may dispatch at confirm, derived from their
   * held capabilities (a subset of VOICE_INTAKE_SUPPORTED_INTENTS).
   */
  allowedIntents: z.array(VoiceIntakeSupportedIntentSchema),
});
export type VoiceIntakeResponse = z.infer<typeof VoiceIntakeResponseSchema>;

// ---------------------------------------------------------------------------
// POST /servicios/voice-intakes/:id/confirm — D9.1/D9.2: the FULL, FINAL
// payload, in the body. The server never reads draft_payload to build the
// domain write. Reuses PurchaseRequestItemSchema-shaped items for repuesto
// (see purchase-request.ts) — same lightweight item contract, no second
// implementation of the same concept.
// ---------------------------------------------------------------------------

/**
 * locx-voz-polish, design.md D4 — one card per falla; the shared asset and
 * meter reading live once at the root of `VoiceIntakeConfirmFallaSchema`.
 */
export const VoiceIntakeConfirmFallaItemSchema = z
  .object({
    description: z.string().min(1),
    failureTypeId: z.string().uuid().optional(),
    importance: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
  })
  .strict();
export type VoiceIntakeConfirmFallaItem = z.infer<typeof VoiceIntakeConfirmFallaItemSchema>;

export const VoiceIntakeConfirmFallaSchema = z
  .object({
    intent: z.literal('falla'),
    assetId: z.string().uuid(),
    odometerHours: z.coerce.number().nonnegative().optional(),
    odometerKm: z.coerce.number().nonnegative().optional(),
    meterReadingMissingReason: z.string().trim().min(1).max(500).optional(),
    notes: z.string().optional(),
    failures: z.array(VoiceIntakeConfirmFallaItemSchema).min(1),
  })
  .strict();
export type VoiceIntakeConfirmFalla = z.infer<typeof VoiceIntakeConfirmFallaSchema>;

/**
 * costCenterId/deliveryLocationId optionality mirrors
 * PurchaseRequestCreateSchema EXACTLY (task 5.6 — verified against the
 * shipped "Solicitar compra" contract rather than inventing a new rule: that
 * endpoint already treats both as optional for a WITH-service-order
 * requisition, so the order-less voice path takes the identical posture).
 */
export const VoiceIntakeConfirmRepuestoItemSchema = z.object({
  description: z.string().min(1).max(1000),
  quantity: z.coerce.number().positive(),
  unit: z.string().min(1).max(32),
  needDate: z.string().datetime({ offset: true }).optional(),
  notes: z.string().max(2000).optional(),
  itemMasterId: z.string().uuid().optional(),
  assetId: z.string().uuid().optional(),
});
export type VoiceIntakeConfirmRepuestoItem = z.infer<typeof VoiceIntakeConfirmRepuestoItemSchema>;

export const VoiceIntakeConfirmRepuestoSchema = z
  .object({
    intent: z.literal('repuesto'),
    /** Present → existing createManualRequisition path; absent → createStandaloneRequisition (D6). */
    serviceOrderId: z.string().uuid().optional(),
    priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
    neededBy: z.string().datetime({ offset: true }).optional(),
    notes: z.string().max(2000).optional(),
    costCenterId: z.string().uuid().optional(),
    deliveryLocationId: z.string().uuid().optional(),
    reference: z.string().max(256).optional(),
    items: z.array(VoiceIntakeConfirmRepuestoItemSchema).min(1, 'At least one item is required'),
  })
  .strict();
export type VoiceIntakeConfirmRepuesto = z.infer<typeof VoiceIntakeConfirmRepuestoSchema>;

/**
 * locx-voz-servicio-cross-tenant, D4/D2 — `servicio`/`servicio_futuro` route
 * through `RequestServiceOrderAsPartnerUseCase` (cross-tenant partner path),
 * NOT `CreateServiceOrderUseCase`. Deliberately NO `clientId` — the
 * operadora's identity is resolved server-side via `resolvePartnerClient`
 * inside the use case's elevated read phase, never taken from the body.
 * Shape matches `RequestServiceOrderAsPartnerInput` exactly (verified).
 */
export const VoiceIntakeConfirmServicioSchema = z
  .object({
    intent: z.literal('servicio'),
    ownerCompanyId: z.string().uuid(),
    serviceTypeId: z.string().uuid(),
    wellName: z.string().max(256).optional(),
    location: z.string().max(256).optional(),
    notes: z.string().max(2000).optional(),
    scheduledAt: z.string().datetime({ offset: true }).optional(),
    fields: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
  })
  .strict();
export type VoiceIntakeConfirmServicio = z.infer<typeof VoiceIntakeConfirmServicioSchema>;

/** `servicio_futuro` twin — identical shape, `scheduledAt` REQUIRED. */
export const VoiceIntakeConfirmServicioFuturoSchema = z
  .object({
    intent: z.literal('servicio_futuro'),
    ownerCompanyId: z.string().uuid(),
    serviceTypeId: z.string().uuid(),
    wellName: z.string().max(256).optional(),
    location: z.string().max(256).optional(),
    notes: z.string().max(2000).optional(),
    scheduledAt: z.string().datetime({ offset: true }),
    fields: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
  })
  .strict();
export type VoiceIntakeConfirmServicioFuturo = z.infer<typeof VoiceIntakeConfirmServicioFuturoSchema>;

export const VoiceIntakeConfirmSchema = z.discriminatedUnion('intent', [
  VoiceIntakeConfirmFallaSchema,
  VoiceIntakeConfirmRepuestoSchema,
  VoiceIntakeConfirmServicioSchema,
  VoiceIntakeConfirmServicioFuturoSchema,
]);
export type VoiceIntakeConfirm = z.infer<typeof VoiceIntakeConfirmSchema>;

export const VoiceIntakeConfirmResponseSchema = z.object({
  voiceIntakeId: z.string().uuid(),
  status: VoiceIntakeStatusSchema,
  dispatchedEntityKind: z.enum(['fleet_failure', 'purchase_case', 'service_order_request']),
  dispatchedEntityId: z.string().uuid(),
});
export type VoiceIntakeConfirmResponse = z.infer<typeof VoiceIntakeConfirmResponseSchema>;

export const VoiceIntakeDiscardResponseSchema = z.object({
  voiceIntakeId: z.string().uuid(),
  status: z.literal('discarded'),
});
export type VoiceIntakeDiscardResponse = z.infer<typeof VoiceIntakeDiscardResponseSchema>;
